import { parse as parseQueryString } from 'query-string';

export interface UtmParams {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  utmTarget?: string;
  gclid?: string;
}

/**
 * Normalizes query-string derived values into deterministic string outputs.
 *
 * @param queryParam - The raw query value (string, string array, null, or undefined).
 */
export function stringOrIgnore(queryParam: unknown): string | undefined {
  if (queryParam === null || queryParam === undefined) {
    return undefined;
  }

  if (Array.isArray(queryParam)) {
    const normalizedValues = queryParam.filter((value): value is string => typeof value === 'string');

    if (!normalizedValues.length) {
      return undefined;
    }

    return normalizedValues[0];
  }

  if (typeof queryParam === 'string') {
    return queryParam;
  }

  return undefined;
}

/**
 * Extract UTM parameters from a URL query string.
 * This utility ensures consistent UTM parameter extraction across the application.
 *
 * @param queryString - The URL query string to parse (e.g., window.location.search).
 *                      If not provided or empty, returns an object with all undefined values.
 * @returns An object containing the extracted UTM parameters
 *
 * @example
 * // In browser context
 * const utmParams = extractUtmParams(window.location.search);
 *
 * @example
 * // With explicit query string
 * const utmParams = extractUtmParams('?utm_source=google&utm_campaign=spring_sale');
 */
export function extractUtmParams(queryString?: string): UtmParams {
  // Return empty params if no query string provided (handles SSR case)
  if (!queryString) {
    return {
      gclid: undefined,
      utmSource: undefined,
      utmMedium: undefined,
      utmCampaign: undefined,
      utmTerm: undefined,
      utmContent: undefined,
      utmTarget: undefined,
    };
  }

  const parsedQs = parseQueryString(queryString);

  return {
    gclid: stringOrIgnore(parsedQs.gclid),
    utmSource: stringOrIgnore(parsedQs.utm_source),
    utmMedium: stringOrIgnore(parsedQs.utm_medium),
    utmCampaign: stringOrIgnore(parsedQs.utm_campaign),
    utmTerm: stringOrIgnore(parsedQs.utm_term),
    utmContent: stringOrIgnore(parsedQs.utm_content),
    utmTarget: stringOrIgnore(parsedQs.utm_target),
  };
}
